import React, { useEffect, useCallback, useMemo } from "react";
import { observer } from "mobx-react-lite";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  Grid2,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  useTheme,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import {
  Receipt as ReceiptIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import DatePicker2 from "../../../../../views/components/common/textFields/DatePicker2";
import ImageUpload from "../../../../../views/components/common/upload/ImageUpload";
import { PurchaseRequestProduct } from "../../../../../models/PurchaseRequest/PurchaseRequestProductInsights";
import { PurchaseRequestSkuVariantRequestBody } from "../../../../../models/PurchaseRequest/PurchaseRequestProductRequestBody";
import {
  PurchaseOrderInvoice,
  InvoiceUploadData,
  InvoiceItemData,
  ProductSelectionState,
} from "../../../../../models/Invoice/PurchaseOrderInvoice";
import dayjs from "dayjs";
import ImageAPI from "../../../api/ImageAPI";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../../../utils/NotificationsContext";
import {
  InvoiceSelectableLine,
  buildInvoiceSelectableLines,
} from "../../../../../utils/purchaseRequestSkuVariants";

interface InvoiceUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onUpload: (uploadData: InvoiceUploadData) => Promise<void>;
  products: PurchaseRequestProduct[];
  skuVariants?: PurchaseRequestSkuVariantRequestBody[];
  invoices?: PurchaseOrderInvoice[];
  poId: number;
  vendorId: number;
  isUploading?: boolean;
}

const InvoiceUploadDialog: React.FC<InvoiceUploadDialogProps> = observer(
  ({
    open,
    onClose,
    onUpload,
    products,
    skuVariants = [],
    invoices = [],
    poId,
    vendorId,
    isUploading = false,
  }) => {
    const theme = useTheme();
    const { invoiceStore } = useStores();
    const { showError } = useNotification();

    const invoiceLines = useMemo(
      () => buildInvoiceSelectableLines(products, skuVariants),
      [products, skuVariants],
    );

    const getRemainingQuantity = useCallback(
      (line: InvoiceSelectableLine): number => {
        const poQuantity = line.quantity || 0;
        let totalInvoicedQuantity = 0;

        invoices.forEach((invoice) => {
          if (invoice.reject_invoice === 1) {
            return;
          }

          const invoiceProducts = invoice.invoice_products || [];
          invoiceProducts.forEach((invoiceProduct) => {
            if (!line.isSkuVariant) {
              if (
                Number(invoiceProduct.product_id) === Number(line.productId)
              ) {
                totalInvoicedQuantity += invoiceProduct.quantity || 0;
              }
              return;
            }

            if (line.variantId != null) {
              if (
                invoiceProduct.variant_id != null &&
                Number(invoiceProduct.variant_id) === Number(line.variantId)
              ) {
                totalInvoicedQuantity += invoiceProduct.quantity || 0;
                return;
              }
              if (
                !invoiceProduct.variant_id &&
                line.skuCode &&
                invoiceProduct.sku_code &&
                invoiceProduct.sku_code === line.skuCode
              ) {
                totalInvoicedQuantity += invoiceProduct.quantity || 0;
              }
              return;
            }

            if (line.skuCode && invoiceProduct.sku_code === line.skuCode) {
              totalInvoicedQuantity += invoiceProduct.quantity || 0;
            }
          });
        });

        return Math.max(0, poQuantity - totalInvoicedQuantity);
      },
      [invoices],
    );

    useEffect(() => {
      if (open && invoiceLines.length > 0) {
        const newSelections = new Map<string, ProductSelectionState>();
        const currentSelections =
          invoiceStore.invoiceUploadState.productSelections;
        invoiceLines.forEach((line) => {
          const remainingQty = getRemainingQuantity(line);
          const rate = line.rate || 0;
          const gstPercentage = line.gstPercentage || 0;
          const baseAmount = remainingQty * rate;
          const gstAmount = (baseAmount * gstPercentage) / 100;
          const totalAmount = baseAmount + gstAmount;

          const existingSelection = currentSelections.get(line.key);
          const shouldPreserveSelection =
            existingSelection?.selected && remainingQty > 0;

          newSelections.set(line.key, {
            selected: shouldPreserveSelection ? true : false,
            quantity:
              shouldPreserveSelection && existingSelection
                ? Math.min(existingSelection.quantity, remainingQty)
                : remainingQty,
            amount:
              shouldPreserveSelection && existingSelection
                ? existingSelection.amount
                : Number(totalAmount.toFixed(2)),
            productId: line.productId,
            variantId: line.isSkuVariant ? line.variantId : null,
            skuCode: line.isSkuVariant ? line.skuCode : null,
          });
        });
        invoiceStore.setProductSelections(newSelections);
      }
    }, [open, invoiceLines, invoices, getRemainingQuantity, invoiceStore]);

    useEffect(() => {
      if (!open) {
        invoiceStore.resetInvoiceUploadState();
      }
    }, [open, invoiceStore]);

    const calculatedTotalAmount = useMemo(() => {
      let total = 0;
      invoiceStore.invoiceUploadState.productSelections.forEach((state) => {
        if (state.selected) {
          total += state.amount;
        }
      });
      if (
        invoiceStore.invoiceUploadState.otherChargesEnabled &&
        invoiceStore.invoiceUploadState.otherChargesAmount
      ) {
        total +=
          Number(invoiceStore.invoiceUploadState.otherChargesAmount) || 0;
      }
      return Number(total.toFixed(2));
    }, [
      invoiceStore.invoiceUploadState.productSelections,
      invoiceStore.invoiceUploadState.otherChargesEnabled,
      invoiceStore.invoiceUploadState.otherChargesAmount,
    ]);

    useEffect(() => {
      invoiceStore.setInvoiceUploadAmount(calculatedTotalAmount.toFixed(2));
    }, [calculatedTotalAmount, invoiceStore]);

    const handleProductToggle = (lineKey: string) => {
      const newMap = new Map(invoiceStore.invoiceUploadState.productSelections);
      const current = newMap.get(lineKey);
      if (current) {
        newMap.set(lineKey, {
          ...current,
          selected: !current.selected,
        });
      }
      invoiceStore.setProductSelections(newMap);
      invoiceStore.setProductsAccordionExpanded(true);
    };

    const handleQuantityChange = (lineKey: string, quantity: number) => {
      const line = invoiceLines.find((item) => item.key === lineKey);
      if (!line) return;

      const remainingQty = getRemainingQuantity(line);
      const validQuantity = Math.min(Math.max(0, quantity), remainingQty);

      const rate = line.rate || 0;
      const gstPercentage = line.gstPercentage || 0;
      const baseAmount = validQuantity * rate;
      const gstAmount = (baseAmount * gstPercentage) / 100;
      const totalAmount = baseAmount + gstAmount;

      const newMap = new Map(invoiceStore.invoiceUploadState.productSelections);
      const currentSelection = newMap.get(lineKey);
      const shouldBeSelected = currentSelection?.selected
        ? validQuantity >= 0
        : validQuantity > 0;

      newMap.set(lineKey, {
        selected: shouldBeSelected,
        quantity: validQuantity,
        amount: Number(totalAmount.toFixed(2)),
        productId: line.productId,
        variantId: line.isSkuVariant ? line.variantId : null,
        skuCode: line.isSkuVariant ? line.skuCode : null,
      });
      invoiceStore.setProductSelections(newMap);
    };

    const handleSelectAll = () => {
      const linesWithRemaining = invoiceLines.filter((line) => {
        const remainingQty = getRemainingQuantity(line);
        return remainingQty > 0;
      });

      const allSelected = linesWithRemaining.every((line) => {
        const selection = invoiceStore.invoiceUploadState.productSelections.get(
          line.key,
        );
        return selection?.selected === true;
      });

      const newMap = new Map(invoiceStore.invoiceUploadState.productSelections);
      linesWithRemaining.forEach((line) => {
        const current = newMap.get(line.key);
        if (current) {
          newMap.set(line.key, {
            ...current,
            selected: !allSelected,
          });
        }
      });
      invoiceStore.setProductSelections(newMap);
      invoiceStore.setProductsAccordionExpanded(true);
    };

    const handleToggleProductGroup = (lines: InvoiceSelectableLine[]) => {
      const selectable = lines.filter((line) => getRemainingQuantity(line) > 0);
      if (selectable.length === 0) return;

      const allGroupSelected = selectable.every((line) => {
        const selection = invoiceStore.invoiceUploadState.productSelections.get(
          line.key,
        );
        return selection?.selected === true;
      });

      const newMap = new Map(invoiceStore.invoiceUploadState.productSelections);
      selectable.forEach((line) => {
        const current = newMap.get(line.key);
        if (current) {
          newMap.set(line.key, {
            ...current,
            selected: !allGroupSelected,
          });
        }
      });
      invoiceStore.setProductSelections(newMap);
      invoiceStore.setProductsAccordionExpanded(true);
    };

    const validateForm = (): boolean => {
      if (!invoiceStore.invoiceUploadState.invoiceFile) {
        return false;
      }
      if (!invoiceStore.invoiceUploadState.invoiceNumber.trim()) {
        return false;
      }
      if (
        !invoiceStore.invoiceUploadState.invoiceAmount ||
        Number(invoiceStore.invoiceUploadState.invoiceAmount) <= 0
      ) {
        return false;
      }
      if (!invoiceStore.invoiceUploadState.invoiceDate) {
        return false;
      }

      const selectedProducts = Array.from(
        invoiceStore.invoiceUploadState.productSelections.values(),
      ).filter((state) => state.selected);

      const hasValidOtherCharges =
        invoiceStore.invoiceUploadState.otherChargesEnabled &&
        invoiceStore.invoiceUploadState.otherChargesAmount &&
        Number(invoiceStore.invoiceUploadState.otherChargesAmount) > 0;

      if (selectedProducts.length === 0 && !hasValidOtherCharges) {
        return false;
      }

      if (selectedProducts.length > 0) {
        if (selectedProducts.some((state) => state.quantity <= 0)) {
          return false;
        }
      }

      if (
        invoiceStore.invoiceUploadState.otherChargesEnabled &&
        (!invoiceStore.invoiceUploadState.otherChargesAmount ||
          Number(invoiceStore.invoiceUploadState.otherChargesAmount) < 0)
      ) {
        return false;
      }

      if (invoiceStore.invoiceUploadState.invoiceDate) {
        if (invoiceStore.invoiceUploadState.invoiceDate.isAfter(dayjs())) {
          return false;
        }
      }

      return true;
    };

    const handleFileUpload = async (file: File): Promise<string> => {
      const imageAPI = new ImageAPI();
      try {
        const uploadedUrls = await imageAPI.uploadVendorDocuments(
          [file],
          "vendor-invoices",
        );
        if (uploadedUrls && uploadedUrls.length > 0) {
          return uploadedUrls[0];
        }
        throw new Error("File upload failed");
      } catch (error) {
        console.error("File upload error:", error);
        throw error;
      }
    };

    const handleUpload = async () => {
      if (!validateForm() || !invoiceStore.invoiceUploadState.invoiceFile) {
        return;
      }

      try {
        const fileUrl = await handleFileUpload(
          invoiceStore.invoiceUploadState.invoiceFile,
        );
        invoiceStore.setInvoiceFileUrl(fileUrl);

        const invoiceItems: InvoiceItemData[] = [];
        invoiceStore.invoiceUploadState.productSelections.forEach((state) => {
          if (state.selected && state.quantity > 0) {
            invoiceItems.push({
              product_id: state.productId,
              quantity: state.quantity,
              amount: state.amount,
              variant_id: state.variantId || null,
              sku_code: state.skuCode || null,
            });
          }
        });

        const uploadData: InvoiceUploadData = {
          po_id: poId,
          vendor_id: vendorId,
          invoice_url: fileUrl,
          invoice_number: invoiceStore.invoiceUploadState.invoiceNumber.trim(),
          invoice_amount: invoiceStore.invoiceUploadState.invoiceAmount.trim(),
          invoice_date: invoiceStore.invoiceUploadState.invoiceDate
            ? invoiceStore.invoiceUploadState.invoiceDate.format("YYYY-MM-DD")
            : "",
          invoice_items: invoiceItems,
          other_charges: invoiceStore.invoiceUploadState.otherChargesEnabled
            ? Number(invoiceStore.invoiceUploadState.otherChargesAmount) || 0
            : undefined,
        };

        await onUpload(uploadData);
      } catch (error: any) {
        showError(
          "Error",
          error?.response?.data?.message ||
            error?.message ||
            "Failed to upload invoice. Please try again later.",
        );
        console.error("Upload error:", error);
      }
    };

    const groupedInvoiceLines = useMemo(() => {
      const groups: Array<{
        productId: number;
        productName: string;
        hsnCode?: string | null;
        hasSkus: boolean;
        lines: InvoiceSelectableLine[];
      }> = [];
      const indexByProduct = new Map<number, number>();

      invoiceLines.forEach((line) => {
        const existingIndex = indexByProduct.get(line.productId);
        if (existingIndex == null) {
          indexByProduct.set(line.productId, groups.length);
          groups.push({
            productId: line.productId,
            productName: line.productName,
            hsnCode: line.hsnCode,
            hasSkus: line.isSkuVariant,
            lines: [line],
          });
          return;
        }
        groups[existingIndex].lines.push(line);
        if (line.isSkuVariant) {
          groups[existingIndex].hasSkus = true;
        }
      });

      return groups;
    }, [invoiceLines]);

    const sortedGroups = useMemo(() => {
      return [...groupedInvoiceLines].sort((a, b) => {
        const remainingA = a.lines.reduce(
          (sum, line) => sum + getRemainingQuantity(line),
          0,
        );
        const remainingB = b.lines.reduce(
          (sum, line) => sum + getRemainingQuantity(line),
          0,
        );
        if (remainingA > 0 && remainingB === 0) return -1;
        if (remainingA === 0 && remainingB > 0) return 1;
        return 0;
      });
    }, [groupedInvoiceLines, getRemainingQuantity]);

    const linesWithRemaining = invoiceLines.filter(
      (line) => getRemainingQuantity(line) > 0,
    );

    const allSelected =
      linesWithRemaining.length > 0 &&
      linesWithRemaining.every((line) => {
        const selection = invoiceStore.invoiceUploadState.productSelections.get(
          line.key,
        );
        return selection?.selected === true;
      });

    const renderLineRow = (
      line: InvoiceSelectableLine,
      remainingQty: number,
      nested: boolean,
    ) => {
      const selection =
        invoiceStore.invoiceUploadState.productSelections.get(line.key);
      if (!selection) return null;

      const showCheckbox = remainingQty > 0;
      const displayName = nested ? line.title : line.productName;

      return (
        <Card
          key={line.key}
          sx={{
            border: selection.selected
              ? `1.5px solid ${theme.palette.primary.main}`
              : `1px solid ${theme.palette.divider}`,
            borderRadius: "6px",
            backgroundColor: selection.selected
              ? theme.palette.primary.main + "05"
              : remainingQty === 0
                ? theme.palette.grey[50]
                : "transparent",
            transition: "all 0.2s ease",
            opacity: remainingQty === 0 ? 0.6 : 1,
            height: "auto",
            minHeight: "auto",
            overflow: "visible",
            flexShrink: 0,
          }}
        >
          <CardContent
            sx={{
              padding: "12px 16px !important",
              height: "auto",
              overflow: "visible",
              "&:last-child": {
                paddingBottom: "12px",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                flexWrap: { xs: "wrap", sm: "nowrap" },
                width: "100%",
                minHeight: 0,
              }}
            >
              {showCheckbox && (
                <Box sx={{ pt: 0.5, flexShrink: 0 }}>
                  <Checkbox
                    checked={selection.selected}
                    onClick={() => handleProductToggle(line.key)}
                    disabled={isUploading}
                    size="small"
                    sx={{
                      padding: 0,
                      "& .MuiSvgIcon-root": { fontSize: 20 },
                    }}
                  />
                </Box>
              )}
              {!showCheckbox && <Box sx={{ width: 24, flexShrink: 0 }} />}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  alignItems: {
                    xs: "flex-start",
                    sm: "flex-start",
                  },
                  width: "100%",
                  flex: { xs: "1 1 auto", sm: 1 },
                  minWidth: 0,
                  minHeight: 0,
                  gap: { xs: 1.5, sm: 2 },
                }}
              >
                <Box
                  sx={{
                    flex: { xs: "1 1 auto", sm: 1 },
                    minWidth: 0,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                  }}
                >
                  <CustomTypography
                    variant="body1"
                    fontWeight={600}
                    style={{ marginBottom: 6, marginTop: 6 }}
                  >
                    {displayName}
                  </CustomTypography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1.5,
                      alignItems: "center",
                      marginBottom: 2,
                    }}
                  >
                    {line.skuCode && line.isSkuVariant && (
                      <Chip
                        label={line.skuCode}
                        size="small"
                        sx={{ height: 18, fontSize: "0.65rem" }}
                      />
                    )}
                    <CustomTypography
                      variant="body2"
                      color="text.secondary"
                      style={{ fontSize: "0.75rem" }}
                    >
                      <strong>Rate:</strong> ₹{line.rate || 0}
                    </CustomTypography>
                    <CustomTypography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "0.75rem" }}
                    >
                      <strong>PO Qty:</strong> {line.quantity || 0}
                    </CustomTypography>
                    <CustomTypography
                      variant="body2"
                      sx={{
                        color: "error.main",
                        fontWeight: 600,
                      }}
                    >
                      <strong>Remaining:</strong> {remainingQty}
                    </CustomTypography>
                    <CustomTypography
                      variant="body2"
                      color="text.secondary"
                      style={{ fontSize: "0.75rem" }}
                    >
                      <strong>GST:</strong> {line.gstPercentage || 0}%
                    </CustomTypography>
                    {!nested && line.hsnCode && (
                      <CustomTypography
                        variant="body2"
                        color="text.secondary"
                        style={{ fontSize: "0.75rem" }}
                      >
                        <strong>HSN:</strong> {line.hsnCode}
                      </CustomTypography>
                    )}
                  </Box>
                </Box>

                {selection.selected && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: {
                        xs: "column",
                        sm: "row",
                      },
                      gap: { xs: 1, sm: 1.5 },
                      alignItems: {
                        xs: "stretch",
                        sm: "flex-start",
                      },
                      flexShrink: 0,
                      flexGrow: 0,
                      width: { xs: "100%", sm: "auto" },
                      mt: { xs: 1.5, sm: 0 },
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: "100%", sm: "100px" },
                        minWidth: { xs: "100%", sm: "100px" },
                      }}
                    >
                      <CustomTypography
                        variant="body2"
                        style={{
                          marginBottom: 8,
                          fontSize: "0.75rem",
                          fontWeight: 600,
                        }}
                      >
                        Quantity{" "}
                        <span
                          style={{
                            color: theme.palette.error.main,
                          }}
                        >
                          *
                        </span>
                      </CustomTypography>
                      <CustomTextField
                        type="number"
                        width="100%"
                        value={selection.quantity.toString()}
                        onChange={(
                          e: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          const inputValue = e.target.value;

                          if (inputValue === "" || inputValue === "-") {
                            handleQuantityChange(line.key, 0);
                            return;
                          }
                          const qty = Number(inputValue);
                          if (isNaN(qty)) {
                            return;
                          }

                          if (qty > remainingQty) {
                            handleQuantityChange(line.key, remainingQty);
                            return;
                          }
                          handleQuantityChange(line.key, qty);
                        }}
                        disabled={isUploading}
                        allowDecimalValue={false}
                        allowNegativeValue={false}
                        error={
                          selection.quantity > remainingQty ||
                          selection.quantity <= 0
                        }
                      />
                    </Box>
                    <Box
                      sx={{
                        width: { xs: "100%", sm: "120px" },
                        minWidth: { xs: "100%", sm: "120px" },
                      }}
                    >
                      <CustomTypography
                        variant="body2"
                        style={{
                          marginBottom: 8,
                          fontSize: "0.75rem",
                          fontWeight: 600,
                        }}
                      >
                        Amount
                      </CustomTypography>
                      <CustomTextField
                        value={`₹${selection.amount.toFixed(2)}`}
                        disabled
                        width="100%"
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      );
    };

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        slotProps={{
          transition: {
            timeout: 300,
            sx: {
              backgroundColor: theme.palette.customColors.backdrop,
              backdropFilter: "blur(4px)",
            },
          },
          backdrop: {
            timeout: 300,
            sx: {
              backgroundColor: theme.palette.customColors.backdrop,
              backdropFilter: "blur(4px)",
            },
          },
          paper: {
            sx: {
              borderRadius: "16px",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
              overflow: "hidden",
              zIndex: 2000,
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            },
          },
        }}
        sx={{
          zIndex: 2000,
          "& .MuiDialog-container": {
            alignItems: "center",
            justifyContent: "center",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            maxHeight: "90vh",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: `linear-gradient(90deg, ${theme.palette.customColors?.blueGradientStart} 0%, ${theme.palette.customColors?.blueGradientEnd} 50%, ${theme.palette.customColors?.blueGradientStart} 100%)`,
            },
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
              borderBottom: `1px solid ${theme.palette.customColors?.greyBorder || theme.palette.divider}`,
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.primary.main + "15",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ReceiptIcon sx={{ color: "primary.main", fontSize: 20 }} />
              </Box>
              <Box>
                <CustomTypography
                  variant="heading4"
                  fontWeight={600}
                  color={
                    theme.palette.customColors?.blackBorder || "text.primary"
                  }
                >
                  Upload Invoice
                </CustomTypography>
                <CustomTypography
                  variant="body2"
                  color={
                    theme.palette.customColors?.greyTextColor ||
                    "text.secondary"
                  }
                  sx={{ mt: 0.5 }}
                >
                  Fill in the invoice details below
                </CustomTypography>
              </Box>
            </Box>
            <IconButton
              onClick={onClose}
              disabled={isUploading}
              sx={{
                color:
                  theme.palette.customColors?.greyTextColor ||
                  theme.palette.text.secondary,
                "&:hover": {
                  backgroundColor:
                    theme.palette.customColors?.lightGreyColor ||
                    theme.palette.grey[100],
                  color:
                    theme.palette.customColors?.blackBorder ||
                    theme.palette.text.primary,
                },
                "&:disabled": {
                  opacity: 0.5,
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <DialogContent
            sx={{
              padding: { xs: "20px", sm: "24px" },
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor:
                  theme.palette.customColors?.greyBackground ||
                  theme.palette.grey[100],
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor:
                  theme.palette.customColors?.greyDarkborder ||
                  theme.palette.grey[400],
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor:
                    theme.palette.customColors?.greyTextColor ||
                    theme.palette.grey[600],
                },
              },
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Basic Invoice Information */}
              <Grid2 container spacing={2.5}>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <CustomTypography
                      variant="heading6"
                      style={{ fontWeight: 600, marginBottom: 16 }}
                    >
                      Invoice Number{" "}
                      <span
                        style={{
                          color: theme.palette.error.main,
                          marginLeft: 4,
                        }}
                      >
                        *
                      </span>
                    </CustomTypography>
                    <CustomTextField
                      label="Invoice Number"
                      placeholder="Enter Invoice Number"
                      value={invoiceStore.invoiceUploadState.invoiceNumber}
                      width="100%"
                      onChange={(e) =>
                        invoiceStore.setInvoiceUploadNumber(e.target.value)
                      }
                      disabled={isUploading}
                    />
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <CustomTypography
                      variant="heading6"
                      style={{ fontWeight: 600, marginBottom: 16 }}
                    >
                      Invoice Date{" "}
                      <span
                        style={{
                          color: theme.palette.error.main,
                          marginLeft: 4,
                        }}
                      >
                        *
                      </span>
                    </CustomTypography>
                    <DatePicker2
                      label="Invoice Date"
                      value={invoiceStore.invoiceUploadState.invoiceDate}
                      onChange={(date) =>
                        invoiceStore.setInvoiceUploadDate(date)
                      }
                      disabled={isUploading}
                      maxDate={dayjs()}
                    />
                  </Box>
                </Grid2>
              </Grid2>

              <Accordion
                expanded={
                  invoiceStore.invoiceUploadState.productsAccordionExpanded
                }
                onChange={(_, expanded) =>
                  invoiceStore.setProductsAccordionExpanded(expanded)
                }
                sx={{
                  boxShadow: "none",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "8px",
                  overflow: "visible",
                  "&:before": {
                    display: "none",
                  },
                  "&.Mui-expanded": {
                    margin: 0,
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    minHeight: 48,
                    "&.Mui-expanded": {
                      minHeight: 48,
                    },
                    "& .MuiAccordionSummary-content": {
                      margin: "12px 0",
                      "&.Mui-expanded": {
                        margin: "12px 0",
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    <CustomTypography variant="heading6" fontWeight={600}>
                      Select Products{" "}
                      <span
                        style={{
                          color: theme.palette.error.main,
                          marginLeft: 4,
                        }}
                      >
                        *
                      </span>
                    </CustomTypography>
                    {linesWithRemaining.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          marginRight: 2,
                        }}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      >
                        <AppButton
                          label={allSelected ? "Clear All" : "Select All"}
                          variant="grayscale"
                          onClick={handleSelectAll}
                          disabled={isUploading}
                          customStyles={{
                            fontSize: "0.75rem",
                            padding: "4px 12px",
                            minHeight: "28px",
                            textTransform: "none",
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: "0 16px 16px 16px",
                    overflow: "visible",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      maxHeight: { xs: "45vh", sm: "400px" },
                      overflowY: "auto",
                      overflowX: "hidden",
                      padding: "4px",
                      pr: 1,
                      "&::-webkit-scrollbar": {
                        width: "8px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor:
                          theme.palette.customColors?.greyBackground ||
                          theme.palette.grey[100],
                        borderRadius: "4px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor:
                          theme.palette.customColors?.greyDarkborder ||
                          theme.palette.grey[400],
                        borderRadius: "4px",
                        "&:hover": {
                          backgroundColor:
                            theme.palette.customColors?.greyTextColor ||
                            theme.palette.grey[600],
                        },
                      },
                    }}
                  >
                    {sortedGroups.map((group) => {
                      const lineStates = group.lines.map((line) => ({
                        line,
                        remainingQty: getRemainingQuantity(line),
                      }));
                      const selectable = lineStates.filter(
                        (item) => item.remainingQty > 0,
                      );
                      const selectedCount = selectable.filter((item) => {
                        const selection =
                          invoiceStore.invoiceUploadState.productSelections.get(
                            item.line.key,
                          );
                        return selection?.selected === true;
                      }).length;
                      const allGroupSelected =
                        selectable.length > 0 &&
                        selectedCount === selectable.length;
                      const someGroupSelected =
                        selectedCount > 0 && !allGroupSelected;

                      if (!group.hasSkus) {
                        const firstLine = lineStates[0];
                        if (!firstLine) return null;
                        return renderLineRow(
                          firstLine.line,
                          firstLine.remainingQty,
                          false,
                        );
                      }

                      return (
                        <Card
                          key={group.productId}
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: "6px",
                            boxShadow: "none",
                            overflow: "visible",
                            flexShrink: 0,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              px: 1.5,
                              py: 1,
                              backgroundColor:
                                theme.palette.customColors?.greyBackground ||
                                theme.palette.grey[50],
                              borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Checkbox
                              size="small"
                              checked={allGroupSelected}
                              indeterminate={someGroupSelected}
                              onChange={() =>
                                handleToggleProductGroup(group.lines)
                              }
                              disabled={isUploading || selectable.length === 0}
                              sx={{
                                padding: 0,
                                "& .MuiSvgIcon-root": { fontSize: 20 },
                              }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                              <CustomTypography
                                variant="body1"
                                fontWeight={700}
                                style={{ fontSize: "0.875rem" }}
                              >
                                {group.productName}
                              </CustomTypography>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 1,
                                  mt: 0.25,
                                }}
                              >
                                <CustomTypography
                                  variant="body2"
                                  color="text.secondary"
                                  style={{ fontSize: "0.75rem" }}
                                >
                                  {group.lines.length} SKU
                                  {group.lines.length === 1 ? "" : "s"}
                                </CustomTypography>
                                {group.hsnCode && (
                                  <CustomTypography
                                    variant="body2"
                                    color="text.secondary"
                                    style={{ fontSize: "0.75rem" }}
                                  >
                                    <strong>HSN:</strong> {group.hsnCode}
                                  </CustomTypography>
                                )}
                                {selectedCount > 0 && (
                                  <CustomTypography
                                    variant="body2"
                                    color="primary"
                                    style={{ fontSize: "0.75rem" }}
                                  >
                                    {selectedCount} selected
                                  </CustomTypography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              p: 1,
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            {lineStates.map(({ line, remainingQty }) =>
                              renderLineRow(line, remainingQty, true),
                            )}
                          </Box>
                        </Card>
                      );
                    })}
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        invoiceStore.invoiceUploadState.otherChargesEnabled
                      }
                      onChange={(e) =>
                        invoiceStore.setOtherChargesEnabled(e.target.checked)
                      }
                      disabled={isUploading}
                    />
                  }
                  label={
                    <CustomTypography variant="heading6" fontWeight={600}>
                      Other Charges
                    </CustomTypography>
                  }
                />
                <Box>
                  {invoiceStore.invoiceUploadState.otherChargesEnabled && (
                    <Box>
                      <CustomTextField
                        label="Other Charges Amount"
                        placeholder="Enter amount"
                        value={
                          invoiceStore.invoiceUploadState.otherChargesAmount
                        }
                        onChange={(e) =>
                          invoiceStore.setOtherChargesAmount(e.target.value)
                        }
                        type="number"
                        allowDecimalValue={true}
                        allowNegativeValue={false}
                        disabled={isUploading}
                        width="100%"
                      />
                    </Box>
                  )}
                </Box>
              </Box>

              <Grid2 container spacing={2.5}>
                <Grid2 size={{ xs: 12 }}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <CustomTypography
                      variant="heading6"
                      style={{ fontWeight: 600, marginBottom: 16 }}
                    >
                      Total Invoice Amount{" "}
                      <span
                        style={{
                          color: theme.palette.error.main,
                          marginLeft: 4,
                        }}
                      >
                        *
                      </span>
                    </CustomTypography>
                    <CustomTextField
                      label="Total Invoice Amount"
                      placeholder="Auto-calculated"
                      value={invoiceStore.invoiceUploadState.invoiceAmount}
                      width="100%"
                      disabled={true}
                      type="number"
                      allowDecimalValue={true}
                      allowNegativeValue={false}
                      helperText={
                        invoiceStore.invoiceUploadState.otherChargesEnabled &&
                        invoiceStore.invoiceUploadState.otherChargesAmount &&
                        Array.from(
                          invoiceStore.invoiceUploadState.productSelections.values(),
                        ).filter((s) => s.selected).length === 0
                          ? "Auto-calculated from other charges"
                          : `Auto-calculated from selected products${invoiceStore.invoiceUploadState.otherChargesEnabled && invoiceStore.invoiceUploadState.otherChargesAmount ? " and other charges" : ""}`
                      }
                    />
                  </Box>
                </Grid2>
              </Grid2>

              {/* File Upload */}
              <Box>
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 2 }}
                >
                  Invoice File{" "}
                  <span
                    style={{ color: theme.palette.error.main, marginLeft: 4 }}
                  >
                    *
                  </span>
                </CustomTypography>
                <ImageUpload
                  showImagesList={true}
                  existingImageList={[]}
                  newImageList={
                    invoiceStore.invoiceUploadState.invoiceFile
                      ? [invoiceStore.invoiceUploadState.invoiceFile]
                      : []
                  }
                  addImagesToBeUploaded={(files) => {
                    if (files.length > 0) {
                      invoiceStore.setInvoiceUploadFile(files[0]);
                    }
                  }}
                  removeImage={() => {
                    invoiceStore.setInvoiceUploadFile(null);
                  }}
                  size="large"
                  multiple={false}
                  allowedFileTypes=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  disabled={isUploading}
                  minHeight="120px"
                />
              </Box>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              padding: { xs: "10px 16px 10px", sm: "16px 20px 20px" },
              borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
              backgroundColor: theme.palette.customColors.lightGreyColor,
              justifyContent: "flex-end",
              gap: 1,
              flexShrink: 0,
            }}
          >
            <AppButton
              onClick={onClose}
              variant="grayscale"
              label="Cancel"
              customStyles={{
                borderColor: theme.palette.customColors.greyDarkborder,
                color: theme.palette.customColors.greyTextColor,
                fontWeight: 600,
                padding: "8px 20px",
                borderRadius: "8px",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                },
              }}
              disabled={isUploading}
            />

            <AppButton
              onClick={handleUpload}
              variant="primary"
              label="Upload"
              disabled={isUploading || !validateForm()}
              startIcon={
                isUploading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            />
          </DialogActions>
        </Box>
      </Dialog>
    );
  },
);

export default InvoiceUploadDialog;
